import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { getEffectiveDefaultInsightsLens } from "../../../../../constants/default-insights-lens";
import FloatingButtons from "../../../../../parts/FloatingButtons";
import Select from "../../../../../parts/inputs/select";
import { LOOM_URL } from "../../../../../parts/modals/ClientConfigAnnouncementModal";
import { AssignClientConfigurationTemplateModal } from "../../../../../parts/modals/profile/AssignClientConfigurationTemplateModal";
import { CreateClientConfigReviewModal } from "../../../../../parts/modals/profile/CreateClientConfigReviewModal";
import { TemplateConflictModal } from "../../../../../parts/modals/profile/TemplateConflictModal";
import SvgIcon from "../../../../../parts/SvgIcon";
import ToolTip from "../../../../../parts/tooltip";
import type {
  BodyMeasurementsItem,
  BodyPartKey,
  ClientConfiguration as ClientConfigurationType,
  SectionKey,
  SectionMode,
  TrackerKey,
  WeekdayIndex,
} from "../../../../../types/clientConfig";
import {
  getInsightsLensLabel,
  getInsightsLensOptions,
} from "../../../../../utils/insights-lens";
import { CoachMonitoringOverride } from "./CoachMonitoringOverride";
import { ConfigSwitch } from "./ConfigSwitch";
import { clientConfigRoleLabels } from "./i18n";
import { SaveAsTemplateModal } from "./SaveAsTemplateModal";
import { SectionRadioGroups } from "./SectionRadioGroups";
import { SectionToggleCard } from "./SectionToggleCard";
import { TrackingItems } from "./TrackingItems";
import { TurnOffGoalsConfirmModal } from "./TurnOffGoalsConfirmModal";
import { useVisibleSections } from "./useVisibleSections";
import {
  BODY_PART_KEYS,
  type ClientConfigWidgetAvailability,
  DEFAULT_TEMPLATE_TYPES,
  getSectionWidgetAvailability,
  TRACKER_KEYS,
  withReadinessForced,
} from "./utils";

type SectionBlock = {
  error: string;
  messageKey: string;
  deleteUrl?: string;
};

const SINGLE_SECTION_DEFAULT_ORDER: Record<
  "goals" | "femaleCycleTracking" | "yourPlans",
  number
> = {
  goals: 4,
  femaleCycleTracking: 5,
  yourPlans: 7,
};

export const ClientConfiguration = ({
  athlete,
  startingCalendarDay = 0,
  clientConfiguration,
  activeUserToRequest,
  clientConfigurationTemplates,
  clientConfigWidgetAvailability,
  clientGoals = [],
  sectionBlocks = {},
  coachMonitoringSettings,
  coachMonitoringWorkflow,
  coachPreferences,
  orgOwner,
}: {
  athlete: { _id: string };
  /**
   * Logged-in coach's own `startingCalendarDay` (0 = Sun … 6 = Sat) — deliberately not the
   * athlete's or the organization coach's; only reorders weekday chip display.
   */
  startingCalendarDay?: number;
  clientConfiguration: ClientConfigurationType | null;
  activeUserToRequest: { _id: string; referAsClients?: boolean };
  clientConfigurationTemplates?: ClientConfigurationType[];
  clientConfigWidgetAvailability?: ClientConfigWidgetAvailability;
  clientGoals?: unknown[];
  sectionBlocks?: Partial<Record<SectionKey, SectionBlock | null>>;
  coachMonitoringSettings?: {
    morningBrief?: { sendTimeOverride?: string };
  };
  coachMonitoringWorkflow?: {
    options?: { morningBrief?: { status?: string } };
  };
  coachPreferences?: { defaultInsightsLens?: string | null };
  orgOwner?: { defaultInsightsLens?: string | null };
}) => {
  const { t } = useTranslation(["athlete-portal", "common", "dashboard"]);
  const referAsClients = activeUserToRequest?.referAsClients || false;
  const roleLabels = clientConfigRoleLabels(t, referAsClients);

  const [initialConfig, setInitialConfig] = useState(clientConfiguration);
  const [stateConfig, setStateConfig] =
    useState<ClientConfigurationType>(clientConfiguration);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [modalTemplateId, setModalTemplateId] = useState<string>("");
  // True while the coach is drafting a brand-new config for a client that has
  // never had one. The config only exists locally until they hit Save.
  const [pendingCreate, setPendingCreate] = useState(false);
  const [showCreateReviewModal, setShowCreateReviewModal] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState("");
  const [saveTemplateAsDefault, setSaveTemplateAsDefault] = useState(false);
  const [saveTemplateToCurrentClient, setSaveTemplateToCurrentClient] =
    useState(false);
  const [showTurnOffGoalsModal, setShowTurnOffGoalsModal] = useState(false);
  const [confirmGoals, setConfirmGoals] = useState(false);
  const [showTemplateConflictModal, setShowTemplateConflictModal] =
    useState(false);
  const [templateConflictingSections, setTemplateConflictingSections] =
    useState<SectionKey[]>([]);

  // Coach Monitoring's per-client send-time override — a separate backend
  // resource (`coach-monitoring-settings`, not `client-configurations`) that
  // shares this page's single Save/Discard button rather than having its own.
  const [initialSendTimeOverride, setInitialSendTimeOverride] = useState(
    coachMonitoringSettings?.morningBrief?.sendTimeOverride ?? ""
  );
  const [sendTimeOverride, setSendTimeOverride] = useState(
    initialSendTimeOverride
  );

  useEffect(() => {
    setInitialConfig(clientConfiguration);
    setStateConfig(clientConfiguration);
    setSelectedTemplateId(clientConfiguration?.templateId ?? "");
  }, [clientConfiguration]);

  useEffect(() => {
    const value = coachMonitoringSettings?.morningBrief?.sendTimeOverride ?? "";
    setInitialSendTimeOverride(value);
    setSendTimeOverride(value);
  }, [coachMonitoringSettings]);

  const {
    isTrainingVisible,
    isTrainingClientOverride,
    isGoalsVisible,
    isGoalsClientOverride,
    isYourPlansVisible,
    isYourPlansClientOverride,
    isFemaleCycleEnabled,
    isFemaleCycleClientOverride,
    activeTrackerCount,
    isTrackingEnabled,
    sectionClientOverrides,
  } = useVisibleSections({ clientConfig: stateConfig });

  const canSubmit = useMemo(
    () =>
      !isEqual(initialConfig, stateConfig) ||
      sendTimeOverride !== initialSendTimeOverride,
    [initialConfig, stateConfig, sendTimeOverride, initialSendTimeOverride]
  );

  // Unsaved-changes guard: intercepts tab close/refresh and in-app link clicks.
  // Browser back/forward is intentionally left uncovered.
  useEffect(() => {
    if (!canSubmit) return;

    const unsavedChangesMessage =
      "You have unsaved changes. Are you sure you want to leave?";

    // Tab close / refresh — browsers only show their own generic dialog here.
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      return true;
    };

    // In-app <a> clicks — capture phase runs before Rill's delegated click handler.
    const handleClick = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement).closest("a");
      if (!anchor?.href) return;
      const url = new URL(anchor.href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      if (
        url.pathname + url.search ===
        window.location.pathname + window.location.search
      )
        return;
      if (!confirm(unsavedChangesMessage)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("click", handleClick, true);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("click", handleClick, true);
    };
  }, [canSubmit]);

  const setSingleSectionMode = useCallback(
    (key: "goals" | "femaleCycleTracking" | "yourPlans", enabled: boolean) => {
      setSelectedTemplateId("");
      setStateConfig((prev: ClientConfigurationType) => {
        const next = cloneDeep(prev);
        if (!next.sections[key]) {
          next.sections[key] = {
            mode: key === "goals" ? "coach" : "hidden",
            clientOptIn: false,
            order: SINGLE_SECTION_DEFAULT_ORDER[key],
          };
        }
        next.sections[key].mode = enabled
          ? key === "goals"
            ? "coach"
            : "client"
          : "hidden";
        // reset the client opt in to false when the mode is changed
        next.sections[key].clientOptIn = false;
        return next;
      });
    },
    []
  );

  const handleGoalsToggle = useCallback(
    (value: boolean) => {
      if (!value && clientGoals.length > 0) {
        setShowTurnOffGoalsModal(true);
        return;
      }
      setSingleSectionMode("goals", value);
    },
    [clientGoals.length, setSingleSectionMode]
  );

  const setSectionMode = useCallback((key: SectionKey, mode: SectionMode) => {
    setSelectedTemplateId("");
    setStateConfig((prev: ClientConfigurationType) => {
      const next = cloneDeep(prev);
      const existing = prev.sections?.[key];
      if (!next.sections[key]) {
        next.sections[key] = {
          clientOptIn: existing?.clientOptIn ?? false,
          order: existing?.order ?? 0,
          mode,
        };
      } else {
        next.sections[key].mode = mode;
        // reset the client opt in to false when the mode is changed
        next.sections[key].clientOptIn = false;
      }
      return next;
    });
  }, []);

  const setTrackerCoachRequired = useCallback(
    (key: TrackerKey, newStatus: boolean) => {
      setSelectedTemplateId("");
      setStateConfig((prev: ClientConfigurationType) => {
        const next = cloneDeep(prev);
        if (!next.sections.tracking.items[key]) {
          next.sections.tracking.items[key] = {
            coachRequired: false,
            clientEnabled: false,
            order: TRACKER_KEYS.indexOf(key),
          };
        }
        const item = next.sections.tracking.items[key];
        if (newStatus) {
          item.coachRequired = true;
          item.clientEnabled = false;
          if (key !== "readiness" && key !== "sessionRpe") {
            const currentSchedule = item.schedule;
            if (!currentSchedule?.length) item.schedule = [1, 3, 5];
          }
          // Re-enabling with no body parts left selected (only reachable after the
          // auto-off below) defaults back to tracking everything.
          if (key === "bodyMeasurements") {
            const bmItem = item as BodyMeasurementsItem;
            if (!bmItem.bodyParts?.length) {
              bmItem.bodyParts = [...BODY_PART_KEYS];
            }
          }
        } else {
          item.coachRequired = false;
          item.clientEnabled = false;
        }
        return next;
      });
    },
    []
  );

  /**
   * Sets the forced status for the readiness tracker.
   * @param newStatus - The new status for the readiness tracker.
   */
  const setReadinessForced = useCallback((newStatus: boolean) => {
    setSelectedTemplateId("");
    setStateConfig((prev: ClientConfigurationType) =>
      withReadinessForced(prev, newStatus)
    );
  }, []);

  const setTrackerSchedule = useCallback(
    (key: TrackerKey, schedule: WeekdayIndex[]) => {
      if (key === "readiness" || key === "sessionRpe") return;
      setSelectedTemplateId("");
      setStateConfig((prev: ClientConfigurationType) => {
        const next = cloneDeep(prev);
        if (!next.sections.tracking.items[key]) return next;
        next.sections.tracking.items[key].schedule = schedule;
        return next;
      });
    },
    []
  );

  const setBodyMeasurementsParts = useCallback((parts: BodyPartKey[]) => {
    setSelectedTemplateId("");
    setStateConfig((prev: ClientConfigurationType) => {
      const next = cloneDeep(prev);
      if (!next.sections.tracking.items.bodyMeasurements) return next;
      const item = next.sections.tracking.items
        .bodyMeasurements as BodyMeasurementsItem;
      item.bodyParts = parts;
      // A coach edit here is the coach saying "track THIS instead" — it overrules
      // whatever the client had elected on their own, so drop the client's override.
      delete item.clientBodyParts;
      // No body parts left to track — turn the whole tracker off, same as flipping
      // its switch. It can't stay enabled with nothing selected.
      if (parts.length === 0) {
        item.coachRequired = false;
        item.clientEnabled = false;
      }
      return next;
    });
  }, []);

  const coachAssignableTemplates = useMemo(
    () =>
      (clientConfigurationTemplates ?? []).filter(
        (template) =>
          !DEFAULT_TEMPLATE_TYPES.includes(template.templateType) &&
          !template.isForeignAssignment
      ),
    [clientConfigurationTemplates]
  );

  const initialForeignTemplateId = useMemo(() => {
    const templateId = initialConfig?.templateId;
    if (!templateId) return null;
    if (DEFAULT_TEMPLATE_TYPES.includes(initialConfig?.templateType ?? "")) {
      return null;
    }
    const isCoachTemplate = coachAssignableTemplates.some(
      (template) => template._id === templateId
    );
    return isCoachTemplate ? null : templateId;
  }, [
    initialConfig?.templateId,
    initialConfig?.templateType,
    coachAssignableTemplates,
  ]);

  const initialForeignTemplateName = useMemo(() => {
    if (!initialForeignTemplateId) return null;
    const fromList = (clientConfigurationTemplates ?? []).find(
      (template) => template._id === initialForeignTemplateId
    );
    return fromList?.templateName ?? initialConfig?.templateName ?? null;
  }, [
    initialForeignTemplateId,
    clientConfigurationTemplates,
    initialConfig?.templateName,
  ]);
  /**
   * The template that defines the default insights lens for the client.
   */
  const templateDefiningLens = useMemo(() => {
    const templateId = selectedTemplateId || clientConfiguration?.templateId;
    if (!templateId) return null;
    const template = (clientConfigurationTemplates ?? []).find(
      (item) => String(item._id) === String(templateId)
    );
    return template?.defaultInsightsLens ? template : null;
  }, [
    selectedTemplateId,
    clientConfiguration?.templateId,
    clientConfigurationTemplates,
  ]);

  const handleSubmit = useCallback(async () => {
    if (selectedTemplateId.length) {
      const selectedTemplate = coachAssignableTemplates.find(
        (t) => t._id === selectedTemplateId
      );
      const templateSections = selectedTemplate?.sections ?? {};
      const conflicts = (Object.keys(templateSections) as SectionKey[]).filter(
        (section) =>
          templateSections[section]?.mode === "hidden" &&
          sectionBlocks[section] != null
      );

      if (conflicts.length > 0) {
        setModalTemplateId(selectedTemplateId);
        setTemplateConflictingSections(conflicts);
        setShowTemplateConflictModal(true);
        return;
      }

      submit(
        `/actions/client-configuration/reassign-template?athlete=${athlete._id}`,
        {
          method: "POST",
          body: {
            templateId: selectedTemplateId,
          },
        }
      );

      return;
    }

    const putBody: ClientConfigurationType & {
      notifyFemaleCycle?: boolean;
      detach?: boolean;
      morningBriefSendTimeOverride?: string;
    } = { ...stateConfig };
    if (!putBody) return;

    const wasFemaleHidden =
      initialConfig.sections?.femaleCycleTracking?.mode === "hidden";
    const nowFemaleVisible =
      stateConfig.sections?.femaleCycleTracking?.mode !== "hidden";
    const shouldNotifyFemale = wasFemaleHidden && nowFemaleVisible;

    let body = putBody;
    if (shouldNotifyFemale) {
      body = { ...putBody, notifyFemaleCycle: true };
    }

    if (!selectedTemplateId.length && clientConfiguration?.templateId) {
      body = {
        ...body,
        // checking the _id cause otherwise there is nothing to detach from
        ...(clientConfiguration._id ? { detach: true } : {}),
        templateId: undefined,
        templateType: undefined,
      };
    }

    // Only send this when it actually changed — the action's presence, not
    // just its value, is what tells the server to also write the separate
    // `coach-monitoring-settings` doc alongside the client-configuration PUT.
    if (sendTimeOverride !== initialSendTimeOverride) {
      body = { ...body, morningBriefSendTimeOverride: sendTimeOverride };
    }

    submit(`/actions/client-configuration/save?athlete=${athlete._id}`, {
      method: "POST",
      body: confirmGoals ? { ...body, confirm: true } : body,
    });
  }, [
    initialConfig,
    stateConfig,
    athlete._id,
    selectedTemplateId,
    coachAssignableTemplates,
    sectionBlocks,
    confirmGoals,
    clientConfiguration?.templateId,
    clientConfiguration?._id,
    sendTimeOverride,
    initialSendTimeOverride,
  ]);

  const applyTemplateSelection = useCallback(
    (newTemplateId: string) => {
      setSelectedTemplateId(newTemplateId);
      if (!newTemplateId.length) {
        setStateConfig(clientConfiguration);
        return;
      }
      if (newTemplateId === initialForeignTemplateId) {
        setStateConfig(clientConfiguration);
        return;
      }
      const template = coachAssignableTemplates.find(
        (template) => template._id === newTemplateId
      );
      if (!template) return;
      setStateConfig({ ...(template as unknown as ClientConfigurationType) });
    },
    [coachAssignableTemplates, clientConfiguration, initialForeignTemplateId]
  );

  const handleSaveAsTemplate = useCallback(() => {
    if (!stateConfig?.sections) return;
    setSaveTemplateName("");
    setSaveTemplateAsDefault(false);
    setSaveTemplateToCurrentClient(false);
    setShowSaveTemplateModal(true);
  }, [stateConfig?.sections]);

  const handleConfirmSaveAsTemplate = useCallback(() => {
    const name = saveTemplateName.trim();
    if (!name || !stateConfig?.sections) return;
    submit("/actions/client-configuration/save-as-template", {
      method: "POST",
      body: {
        templateName: name,
        sections: stateConfig.sections,
        isDefault: saveTemplateAsDefault,
        defaultInsightsLens: stateConfig.defaultInsightsLens,
        ...(saveTemplateToCurrentClient && { applyToAthleteId: athlete._id }),
      },
    });
    setShowSaveTemplateModal(false);
  }, [
    saveTemplateName,
    stateConfig?.sections,
    stateConfig?.defaultInsightsLens,
    saveTemplateAsDefault,
    saveTemplateToCurrentClient,
    athlete._id,
  ]);

  const handleCreateIndividualConfiguration = useCallback(() => {
    setShowCreateReviewModal(true);
  }, []);

  const handleConfirmCreateReview = useCallback(() => {
    // Skip the empty-state view and show the full editor as a local draft.
    setPendingCreate(true);
    setShowCreateReviewModal(false);
  }, []);

  const handleSavePendingCreate = useCallback(() => {
    submit(`/actions/client-configuration/save?athlete=${athlete._id}`, {
      method: "POST",
      body: {
        sections: stateConfig?.sections,
        quickLog: stateConfig?.quickLog,
        ...(sendTimeOverride !== initialSendTimeOverride
          ? { morningBriefSendTimeOverride: sendTimeOverride }
          : {}),
        defaultInsightsLens: stateConfig?.defaultInsightsLens,
      },
    });
  }, [athlete._id, stateConfig, sendTimeOverride, initialSendTimeOverride]);

  const handleAssignTemplateFromEmptyState = useCallback(() => {
    setModalTemplateId("");
    setShowTemplateModal(true);
  }, []);

  const handleModalAssign = useCallback(() => {
    if (!modalTemplateId) return;

    const selectedTemplate = coachAssignableTemplates.find(
      (t) => t._id === modalTemplateId
    );
    const templateSections = selectedTemplate?.sections ?? {};
    const conflicts = (Object.keys(templateSections) as SectionKey[]).filter(
      (section) =>
        templateSections[section]?.mode === "hidden" &&
        sectionBlocks[section] != null
    );

    if (conflicts.length > 0) {
      setTemplateConflictingSections(conflicts);
      setShowTemplateConflictModal(true);
      return;
    }

    submit(
      `/actions/client-configuration/reassign-template?athlete=${athlete._id}`,
      { method: "POST", body: { templateId: modalTemplateId } }
    );
  }, [modalTemplateId, coachAssignableTemplates, sectionBlocks, athlete._id]);

  const handleConfirmTemplateConflict = useCallback(() => {
    setShowTemplateConflictModal(false);
    submit(
      `/actions/client-configuration/reassign-template?athlete=${athlete._id}`,
      {
        method: "POST",
        body: {
          templateId: modalTemplateId,
          resolveConflicts: true,
          conflictingSections: templateConflictingSections,
        },
      }
    );
  }, [modalTemplateId, templateConflictingSections, athlete._id]);

  const isShowingForeignAssignment = Boolean(
    initialForeignTemplateId && selectedTemplateId === initialForeignTemplateId
  );

  const insightsLensOptions = useMemo(() => getInsightsLensOptions(t), [t]);
  // The athlete's own Progress focus in the app (read-only). Shown only when
  // it differs from the lens chosen here; it never changes this lens.
  const effectiveInsightsLens =
    stateConfig?.defaultInsightsLens ??
    getEffectiveDefaultInsightsLens(coachPreferences, orgOwner);
  const athleteProgressFocus =
    clientConfiguration?.progressFocus &&
    clientConfiguration.progressFocus !== effectiveInsightsLens
      ? clientConfiguration.progressFocus
      : null;

  const templateSelectOptions = useMemo(
    () => [
      {
        value: "",
        label: t("clientConfiguration.template.noneOption", roleLabels),
      },
      ...coachAssignableTemplates.map((template) => ({
        value: template._id,
        label: `${template.templateName}${
          template.isDefault
            ? t("clientConfiguration.template.defaultSuffix")
            : ""
        }`,
      })),
    ],
    [coachAssignableTemplates, roleLabels, t]
  );

  // TODO: work on the empty state
  if (!clientConfiguration) {
    return (
      <section
        className="athlete-profile client-configuration"
        aria-label={t("clientConfiguration.ariaLabel")}
      >
        <header>
          <h4>{t("clientConfiguration.title")}</h4>
          <p className="p5">{t("clientConfiguration.loadingMessage")}</p>
        </header>
      </section>
    );
  }

  // Show the empty state only when the client is still on a product default and
  // no create draft is in progress.
  if (
    !pendingCreate &&
    clientConfiguration &&
    DEFAULT_TEMPLATE_TYPES.includes(stateConfig?.templateType)
  ) {
    return (
      <>
        <section
          className="athlete-profile client-configuration"
          aria-label={t("clientConfiguration.ariaLabel")}
        >
          <div className="empty-state-content">
            <header>
              <h3>{t("clientConfiguration.title")}</h3>
              <p className="p2">
                {t("clientConfiguration.emptyState.description", roleLabels)}
              </p>
            </header>
            <div className="client-config-template-bar__actions">
              <button
                type="button"
                data-button-type="secondary"
                onClick={handleAssignTemplateFromEmptyState}
              >
                {t("clientConfiguration.emptyState.assignTemplate")}
              </button>
              <button
                type="button"
                data-button-type="secondary"
                onClick={() => {
                  submit(`/actions/refresh/`, {
                    method: "GET",
                    body: {
                      "modal[type]": "client-configuration-templates",
                    },
                  });
                }}
              >
                {t("clientConfiguration.emptyState.manageTemplates")}
              </button>
              <button
                type="button"
                data-button-type="primary"
                onClick={handleCreateIndividualConfiguration}
              >
                {t("clientConfiguration.emptyState.createIndividual")}
              </button>
            </div>
          </div>
        </section>
        <CreateClientConfigReviewModal
          isVisible={showCreateReviewModal}
          handleConfirmCreateReview={handleConfirmCreateReview}
          setShowCreateReviewModal={setShowCreateReviewModal}
        />
        <AssignClientConfigurationTemplateModal
          isVisible={showTemplateModal}
          assignableTemplates={coachAssignableTemplates}
          modalTemplateId={modalTemplateId}
          setModalTemplateId={setModalTemplateId}
          setShowTemplateModal={setShowTemplateModal}
          onAssign={handleModalAssign}
        />
      </>
    );
  }

  return (
    <>
      <section
        className="athlete-profile client-configuration"
        aria-label={t("clientConfiguration.ariaLabel")}
      >
        <header>
          <div>
            <h4>{t("clientConfiguration.title")}</h4>
            <p className="p5">
              {t("clientConfiguration.headerDescription", roleLabels)}
            </p>
          </div>
          {/* biome-ignore lint/a11y/useSemanticElements: external link styled as a button */}
          <a
            role="button"
            data-button-type="primary"
            href={LOOM_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("coaching-portal:allTemplates.header.clientConfigTutorial")}
          </a>
        </header>

        <section className="client-config-sections">
          <div className="client-config-card template">
            <div className="client-config-card__left">
              <legend className="client-config-template-bar__label">
                <SvgIcon
                  classNames="shrink-0"
                  src="/img/new-icons/vuesax-linear-document-text.svg"
                />
                <span className="p4 font-semibold">
                  {t("clientConfiguration.template.label")}
                </span>
              </legend>
              <div className="client-config-template-bar__field">
                <label
                  className="sr-only"
                  htmlFor="client-config-template-select"
                >
                  {t("clientConfiguration.template.selectLabel")}
                </label>
                <div className="select client-config-template__select">
                  <Select
                    name="client-config-template-select"
                    disabled={false}
                    options={templateSelectOptions}
                    value={selectedTemplateId}
                    displayLabel={
                      isShowingForeignAssignment
                        ? initialForeignTemplateName ?? undefined
                        : undefined
                    }
                    onChange={applyTemplateSelection}
                    overlay
                  />
                </div>
                <ToolTip
                  id="client-config-template-hint"
                  body={t("clientConfiguration.template.hint", roleLabels)}
                >
                  <button
                    type="button"
                    className="client-config-template__hint"
                    aria-label={t("clientConfiguration.template.hintAriaLabel")}
                  >
                    <SvgIcon
                      classNames="shrink-0"
                      src="/img/new-icons/vuesax-linear-info-circle.svg"
                      noFilterOnLight={true}
                    />
                  </button>
                </ToolTip>
              </div>
            </div>
            <div className="client-config-card__right">
              <div className="client-config-template-bar__row">
                <div className="client-config-template-bar__actions">
                  <button
                    type="button"
                    data-button-type="secondary"
                    onClick={handleSaveAsTemplate}
                    disabled={!!selectedTemplateId}
                  >
                    {t("clientConfiguration.template.saveAsTemplate")}
                  </button>
                  <button
                    type="button"
                    data-button-type="secondary"
                    onClick={() => {
                      submit(`/actions/refresh/`, {
                        method: "GET",
                        body: {
                          "modal[type]": "client-configuration-templates",
                        },
                      });
                    }}
                  >
                    {t("clientConfiguration.template.manageTemplates")}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="client-config-card template">
            <div className="client-config-card__left">
              <legend className="client-config-template-bar__label">
                <SvgIcon
                  classNames="shrink-0"
                  src="/img/new-icons/stats-chart.svg"
                />
                <span className="p4 font-semibold">
                  {t("clientConfiguration.insightsLens.label")}
                </span>
              </legend>
              <div className="client-config-template-bar__field">
                <label
                  className="sr-only"
                  htmlFor="client-config-insights-lens-select"
                >
                  {t("clientConfiguration.insightsLens.selectLabel")}
                </label>
                <div className="select client-config-template__select">
                  <Select
                    name="client-config-insights-lens-select"
                    disabled={false}
                    options={insightsLensOptions}
                    value={effectiveInsightsLens}
                    onChange={(v: string) => {
                      setSelectedTemplateId("");
                      setStateConfig((prev) => ({
                        ...prev,
                        defaultInsightsLens: v,
                      }));
                    }}
                    overlay
                  />
                </div>
                <ToolTip
                  id="client-config-insights-lens-hint"
                  body={
                    templateDefiningLens
                      ? t("clientConfiguration.insightsLens.hintFromTemplate", {
                          templateName: templateDefiningLens.templateName,
                        })
                      : t("clientConfiguration.insightsLens.hint")
                  }
                >
                  <button
                    type="button"
                    className="client-config-template__hint"
                    aria-label={t(
                      "clientConfiguration.insightsLens.hintAriaLabel"
                    )}
                  >
                    <SvgIcon
                      classNames="shrink-0"
                      src="/img/new-icons/vuesax-linear-info-circle.svg"
                      noFilterOnLight={true}
                    />
                  </button>
                </ToolTip>
              </div>
              {athleteProgressFocus && (
                <p className="p6 client-config-card__intro basis-full [contain:inline-size]">
                  {t("clientConfiguration.insightsLens.athleteChoice", {
                    ...roleLabels,
                    lens: getInsightsLensLabel(t, athleteProgressFocus),
                  })}
                </p>
              )}
            </div>
          </div>
          <div id="section-training" className="client-config-card">
            <div className="client-config-card__left">
              <div className="client-config-card__title">
                <span>🏋 {t("clientConfiguration.sections.training")}</span>
                {isTrainingClientOverride && (
                  <span className="chip warning">
                    {t("clientConfiguration.chips.clientOverride", roleLabels)}
                  </span>
                )}
                {isTrainingVisible && sectionBlocks.training === null && (
                  <div className="client-config-card__attention chip warning">
                    <div>
                      <span>
                        {t("clientConfiguration.coachNoData.training")}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {!isTrainingVisible && (
                <span className="chip warning">
                  {t(
                    "clientConfiguration.copy.training.hiddenHint",
                    roleLabels
                  )}
                </span>
              )}
              {isTrainingVisible && (
                <span>
                  {t(
                    "clientConfiguration.copy.training.activeHint",
                    roleLabels
                  )}
                </span>
              )}
            </div>
            <div
              className={`client-config-card__right${
                isTrainingVisible && sectionBlocks.training
                  ? " client-config-switch-blocked"
                  : ""
              }`}
            >
              <ConfigSwitch
                id="client-config-training-visible"
                ariaLabel={t(
                  "clientConfiguration.aria.showTraining",
                  roleLabels
                )}
                checked={isTrainingVisible}
                onChange={(value: boolean) => {
                  setStateConfig((prev: ClientConfigurationType) => {
                    const next = cloneDeep(prev);
                    if (!next.sections.training) {
                      next.sections.training = {
                        mode: "coach",
                        clientOptIn: false,
                        order: 0,
                      };
                    }
                    next.sections.training.mode = value ? "coach" : "hidden";
                    return next;
                  });
                }}
              />
              {isTrainingVisible && sectionBlocks.training && (
                <div className="client-config-switch-blocked__overlay">
                  <span className="chip danger client-config-switch-blocked__chip">
                    {t(`clientConfiguration.sectionBlocks.training`)}
                  </span>
                  {sectionBlocks.training.deleteUrl && (
                    <a
                      href={sectionBlocks.training.deleteUrl}
                      className="client-config-radio__widget-overlay-link"
                    >
                      <span>{t("clientConfiguration.sections.training")}</span>
                      <SvgIcon src="/img/new-icons/arrow-right.svg" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          <SectionRadioGroups
            roleLabels={roleLabels}
            sectionWidgetAvailability={getSectionWidgetAvailability(
              clientConfigWidgetAvailability
            )}
            getSectionMode={(key) => stateConfig?.sections?.[key]?.mode}
            onSectionModeChange={setSectionMode}
            isClientOverride={(key) => !!sectionClientOverrides[key]}
            getBlock={(key) => sectionBlocks[key] ?? null}
          />

          <div className="client-config-card">
            <div className="client-config-card__left">
              <div className="client-config-card__title">
                <span>🎯 {t("clientConfiguration.sections.goals")}</span>
                {isGoalsClientOverride && (
                  <span className="chip warning">
                    {t("clientConfiguration.chips.clientOverride", roleLabels)}
                  </span>
                )}
              </div>

              {!isGoalsVisible && (
                <span>
                  {t("clientConfiguration.copy.goals.hiddenHint", roleLabels)}
                </span>
              )}
              {isGoalsVisible && (
                <span>
                  {t("clientConfiguration.copy.goals.visibleHint", roleLabels)}
                </span>
              )}
            </div>
            <div
              className={`client-config-card__right${
                isGoalsVisible && sectionBlocks.goals
                  ? " client-config-switch-blocked"
                  : ""
              }`}
            >
              <ConfigSwitch
                id="client-config-goals-visible"
                ariaLabel={t("clientConfiguration.aria.showGoals", roleLabels)}
                checked={isGoalsVisible}
                onChange={handleGoalsToggle}
              />
              {isGoalsVisible && sectionBlocks.goals && (
                <div className="client-config-switch-blocked__overlay">
                  <span className="chip danger client-config-switch-blocked__chip">
                    {t(`clientConfiguration.sectionBlocks.goals`)}
                  </span>
                  {sectionBlocks.goals.deleteUrl && (
                    <a
                      href={sectionBlocks.goals.deleteUrl}
                      className="client-config-radio__widget-overlay-link"
                    >
                      <span>{t("clientConfiguration.sections.goals")}</span>
                      <SvgIcon src="/img/new-icons/arrow-right.svg" />
                    </a>
                  )}
                </div>
              )}
            </div>
            {isGoalsVisible && sectionBlocks.goals === null && (
              <div className="client-config-card__attention chip warning">
                <div>
                  <span>{t("clientConfiguration.coachNoData.goals")}</span>
                </div>
              </div>
            )}
          </div>

          <div className="client-config-card tracking">
            <div className="client-config-card__header">
              <div className="client-config-card__left">
                <div className="client-config-card__title">
                  <span>
                    📊 {t("clientConfiguration.sections.trackingRequirements")}
                  </span>
                  {isTrackingEnabled && (
                    <span className="chip info">
                      {t("clientConfiguration.activeTrackers", {
                        count: activeTrackerCount,
                      })}
                    </span>
                  )}
                </div>
              </div>
              <div className="client-config-card__right">
                <ConfigSwitch
                  id="client-config-tracking-enabled"
                  ariaLabel={t(
                    "clientConfiguration.aria.showTracking",
                    roleLabels
                  )}
                  checked={isTrackingEnabled}
                  onChange={(value: boolean) => {
                    setStateConfig((prev: ClientConfigurationType) => {
                      const next = cloneDeep(prev);
                      if (!next.sections.tracking) {
                        next.sections.tracking = {
                          mode: "hidden",
                          clientOptIn: false,
                          order: 5,
                          items: {},
                        };
                      }
                      // Only change mode; preserve all individual tracker settings.
                      next.sections.tracking.mode = value ? "coach" : "hidden";
                      return next;
                    });
                  }}
                />
              </div>
            </div>
            {isTrackingEnabled && (
              <>
                <p className="p6 client-config-card__intro">
                  {t("clientConfiguration.trackingIntro", roleLabels)}
                </p>
                <TrackingItems
                  roleLabels={roleLabels}
                  currentConfig={stateConfig}
                  onTrackerClick={setTrackerCoachRequired}
                  onForcedChange={setReadinessForced}
                  onScheduleChanged={setTrackerSchedule}
                  onBodyPartsChanged={setBodyMeasurementsParts}
                  startingCalendarDay={startingCalendarDay}
                />
              </>
            )}
          </div>

          <SectionToggleCard
            variant="editable"
            icon="🩸"
            titleKey="clientConfiguration.sections.femaleCycle"
            hiddenHintKey="clientConfiguration.copy.femaleCycleTracking.hiddenHint"
            visibleHintKey="clientConfiguration.copy.femaleCycleTracking.visibleHint"
            roleLabels={roleLabels}
            visible={isFemaleCycleEnabled}
            overrideChip={isFemaleCycleClientOverride}
            switchId="client-config-female-cycle"
            ariaLabelKey="clientConfiguration.aria.femaleCycle"
            onChange={(value) =>
              setSingleSectionMode("femaleCycleTracking", value)
            }
          />

          <SectionToggleCard
            variant="editable"
            icon="📅"
            titleKey="clientConfiguration.sections.yourPlans"
            hiddenHintKey="clientConfiguration.copy.yourPlans.hiddenHint"
            visibleHintKey="clientConfiguration.copy.yourPlans.visibleHint"
            roleLabels={roleLabels}
            visible={isYourPlansVisible}
            overrideChip={isYourPlansClientOverride}
            switchId="client-config-your-plans"
            ariaLabelKey="clientConfiguration.aria.yourPlans"
            onChange={(value) => setSingleSectionMode("yourPlans", value)}
          />

          <CoachMonitoringOverride
            coachMonitoringWorkflow={coachMonitoringWorkflow}
            sendTimeOverride={sendTimeOverride}
            onSendTimeOverrideChange={setSendTimeOverride}
          />
        </section>

        <footer>
          <FloatingButtons
            failedText={t("clientConfiguration.buttons.discard")}
            successText={t("common:save")}
            successDisabled={pendingCreate ? false : !canSubmit}
            handleFailed={() => {
              setStateConfig(initialConfig);
              setSelectedTemplateId(initialConfig?.templateId ?? "");
              setSendTimeOverride(initialSendTimeOverride);
            }}
            handleSuccess={
              pendingCreate ? handleSavePendingCreate : handleSubmit
            }
            classNames="client-config-footer-buttons"
            label={t("clientConfiguration.buttons.saveChanges")}
          />
        </footer>
      </section>

      <TemplateConflictModal
        isVisible={showTemplateConflictModal}
        conflictingSections={templateConflictingSections}
        onConfirm={handleConfirmTemplateConflict}
        onCancel={() => setShowTemplateConflictModal(false)}
      />
      <TurnOffGoalsConfirmModal
        roleLabels={roleLabels}
        isVisible={showTurnOffGoalsModal}
        onConfirm={() => {
          setShowTurnOffGoalsModal(false);
          setConfirmGoals(true);
          setSingleSectionMode("goals", false);
        }}
        onCancel={() => setShowTurnOffGoalsModal(false)}
      />
      <SaveAsTemplateModal
        roleLabels={roleLabels}
        isVisible={showSaveTemplateModal}
        saveTemplateName={saveTemplateName}
        setSaveTemplateName={setSaveTemplateName}
        setShowSaveTemplateModal={setShowSaveTemplateModal}
        handleConfirmSaveAsTemplate={handleConfirmSaveAsTemplate}
        setSaveTemplateAsDefault={setSaveTemplateAsDefault}
        saveTemplateAsDefault={saveTemplateAsDefault}
        saveTemplateToCurrentClient={saveTemplateToCurrentClient}
        setSaveTemplateToCurrentClient={setSaveTemplateToCurrentClient}
      />
    </>
  );
};
